import { queryClient } from '@/router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useDebounce } from 'use-debounce'

import { type ComboboxProps } from '@/components/combobox/combobox'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useBooleanState } from '@/lib/hooks/use-boolean-state'
import { caseCategoriesQuery } from '@/lib/queries/business/case-category/queries'
import { serviceCentersQuery } from '@/lib/queries/business/office/queries'
import { reloadCaseDataMutation } from '@/lib/queries/business/v1/ocr/reload/mutations'
import { documentOCRResultQuery } from '@/lib/queries/business/v1/ocr/result/queries'
import { type EnhancedResDocumentOCRResultQuery } from '@/lib/queries/business/v1/ocr/result/types'
import { completeOCRReviewMutation } from '@/lib/queries/business/v1/ocr/review/mutations'
import { type ReqCompleteOCRReviewMutation } from '@/lib/queries/business/v1/ocr/review/types'

import { useDocumentOCRResultStore } from '../../../store'
import { constructOCRDataPayload } from '../../../utils'
import { DOCUMENT_OCR_RESULT_FORM_DEFAULT_VALUES } from './constants'
import { type DocumentOCRResultForm } from './types'

export const useCurrentDocumentForms = () => {
  const { taskId: taskIdSearchParam } = useSearch({ from: '/process-document/review/$jobId' })

  const {
    booleanState: isOpenExtractedInfoGuideDialog,
    setToTrue: openExtractInfoGuideDialog,
    setToFalse: closeExtractInfoGuideDialog,
  } = useBooleanState(false)

  const { ocrResults, getCurrentOCRResult, setOCRResult } = useDocumentOCRResultStore()

  const documentOCRResultForm = useForm<DocumentOCRResultForm>({
    mode: 'onChange',
    defaultValues: DOCUMENT_OCR_RESULT_FORM_DEFAULT_VALUES,
  })

  const currentOCRResult = getCurrentOCRResult(taskIdSearchParam)
  const [debouncedCaseType] = useDebounce(currentOCRResult?.caseType.value, 300)

  const { data: documentOCRResultData } = useQuery(documentOCRResultQuery(taskIdSearchParam))
  const { data: serviceCenterList } = useQuery(serviceCentersQuery)
  const { data: caseCategoryList } = useQuery(caseCategoriesQuery(debouncedCaseType))

  const { mutateAsync: reloadCaseData, isPending: isReloadingCaseData } = useMutation(reloadCaseDataMutation)
  const { mutateAsync: completeOCRReview, isPending: isUpdatingOCRReviewStatus } =
    useMutation(completeOCRReviewMutation)

  const applicantCandidateList = documentOCRResultData?.applicantCandidateList || []
  const hasMatchingApplicant = (applicantCandidateList?.length || 0) > 0
  const hasSetDefaultApplicantRef = useRef<Record<number, boolean>>({})
  const isReviewed = currentOCRResult?.isReviewed
  const isDisabledReloadButton = isReviewed || !currentOCRResult?.caseNumber.value
  const isDisabledCompleteReviewButton = !documentOCRResultForm.formState.isValid

  const handleChangeExtractedInfoGuideDialogOpen = (open: boolean) => {
    open ? openExtractInfoGuideDialog() : closeExtractInfoGuideDialog()
  }

  const handleChangeCaseNumber = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseNumber: {
        ...currentOCRResult.caseNumber,
        text: value,
        value: value,
      },
    })
  }

  const handleChangeNewApplicantName = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      applicantNameCandidate: {
        ...currentOCRResult.applicantNameCandidate,
        ocrScannedApplicantNameWhenNew: value,
      },
    })
  }

  const handleChangeExistingOcrScannedApplicantName = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      applicantNameCandidate: {
        ...currentOCRResult.applicantNameCandidate,
        ocrScannedApplicantNameWhenExisting: value,
      },
    })
  }

  const handleChangeExistingMatchingApplicantId = (id: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }

    const selectedMatchingApplicantInfo = applicantCandidateList?.find(({ applicantId }) => `${applicantId}` === id)

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseAdditionalData: selectedMatchingApplicantInfo
        ? {
            ...currentOCRResult.caseAdditionalData,
            email: selectedMatchingApplicantInfo.applicantEmail,
            phone: selectedMatchingApplicantInfo.applicantPhone,
          }
        : {
            ...currentOCRResult.caseAdditionalData,
            email: '',
            phone: '',
          },
      applicantNameCandidate: {
        ...currentOCRResult.applicantNameCandidate,
        matchingApplicantId: id,
      },
    })
  }

  const handleChangeApplicationFiledDate = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      applicationFiledDate: {
        ...currentOCRResult.applicationFiledDate,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleChangeCaseType = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseType: {
        ...currentOCRResult.caseType,
        text: value,
        value: value,
      },
    })
  }

  const handleChangeServiceCenter: ComboboxProps['onValueChange'] = (value) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }

    const { code, description } = serviceCenterList?.find(({ code }) => code === value) ?? {}
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      serviceCenter: {
        ...currentOCRResult.serviceCenter,
        text: description,
        value: code,
      },
    })
  }

  const handleChangeNationality = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      nationality: {
        ...currentOCRResult.nationality,
        text: value,
      },
    })
  }

  const handleSelectPriorityDate = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      priorityDate: {
        ...currentOCRResult.priorityDate,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleSelectValidFrom = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      validFrom: {
        ...currentOCRResult.validFrom,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleSelectValidTo = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      validTo: {
        ...currentOCRResult.validTo,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleSelectRfeDueDate = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      rfeDueDate: {
        ...currentOCRResult.rfeDueDate,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleSelectInterviewDate = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      interviewDate: {
        ...currentOCRResult.interviewDate,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleSelectBiometricDate = (dateString?: string) => {
    if (!taskIdSearchParam || !currentOCRResult || !dateString) {
      return
    }

    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      biometricDate: {
        ...currentOCRResult.biometricDate,
        text: dateString,
        value: dateString,
      },
    })
  }

  const handleChangeApplicantEmail = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseAdditionalData: {
        ...currentOCRResult.caseAdditionalData,
        email: value,
      },
    })
  }

  const handleChangeApplicantPhone = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseAdditionalData: {
        ...currentOCRResult.caseAdditionalData,
        phone: value,
      },
    })
  }

  const handleChangeCaseTags = (value: string) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseAdditionalData: {
        ...currentOCRResult.caseAdditionalData,
        caseTags: value.split(','),
      },
    })
  }

  const handleChangeCaseCategory: ComboboxProps['onValueChange'] = (value) => {
    if (!taskIdSearchParam || !currentOCRResult) {
      return
    }
    setOCRResult(taskIdSearchParam, {
      ...currentOCRResult,
      caseAdditionalData: {
        ...currentOCRResult.caseAdditionalData,
        caseCategory: value || '',
      },
    })
  }

  const handleClickReloadButton = async () => {
    const caseNumberInputValue = documentOCRResultForm.getValues('caseNumber.value')

    if (!currentOCRResult || !caseNumberInputValue) {
      return
    }

    const { taskId } = currentOCRResult
    await reloadCaseData(
      {
        taskId,
        caseNumber: caseNumberInputValue,
      },
      {
        onSuccess: (response) => {
          toast.success('Case data reloaded successfully.', {
            position: 'top-center',
          })

          if (taskIdSearchParam) {
            setOCRResult(taskIdSearchParam, {
              ...response,
              applicantNameCandidate: {
                ocrScannedApplicantNameWhenNew: response.applicantName.value || '',
                ocrScannedApplicantNameWhenExisting: response.applicantName.value || '',
                matchingApplicantId: undefined,
              },
              caseAdditionalData: {
                ...currentOCRResult.caseAdditionalData,
              },
            })
          }
        },
        onError: () => {
          toast.error('Failed to reload case data.', {
            position: 'top-center',
          })
        },
      }
    )
  }

  const handleClickCompleteReviewButton = async () => {
    if (!currentOCRResult) {
      return
    }

    const { taskId, isReviewed } = currentOCRResult

    const payload: ReqCompleteOCRReviewMutation = isReviewed
      ? {
          isComplete: false,
          reviewData: {
            taskId,
          },
        }
      : {
          isComplete: true,
          reviewData: constructOCRDataPayload(currentOCRResult),
        }

    await completeOCRReview(payload, {
      onSuccess: () => {
        if (isReviewed) {
          toast.success('Review has been cancelled.', {
            position: 'top-center',
          })
        } else {
          toast.success(`"${currentOCRResult.fileName}" review has been completed.`, {
            position: 'top-center',
            className: 'w-[500px]',
          })
        }

        if (taskIdSearchParam) {
          setOCRResult(taskIdSearchParam, {
            ...currentOCRResult,
            isReviewed: !isReviewed,
          })
        }

        void queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.business.v1.ocr.task.index, currentOCRResult.jobId],
        })
      },
      onError: () => {
        toast.error('Failed to complete review.', {
          position: 'top-center',
        })
      },
    })
  }

  const setFormErrors = (currentOCRResult: EnhancedResDocumentOCRResultQuery) => {
    if (currentOCRResult.caseNumber.value?.length === 0) {
      documentOCRResultForm.setError('caseNumber', {
        type: 'required',
        message: 'Case Number is required',
      })
    }
  }

  /**
   * NOTE:
   *   Server responds with just ONLY one applicant candidate when there is exact matching applicant.
   *   So, we can set the default value of matching applicant id to the only one applicant candidate id.
   */
  const setDefaultApplicant = (taskId: number, ocrResultData: EnhancedResDocumentOCRResultQuery) => {
    const { applicantName, applicantCandidateList } = ocrResultData
    const isNewField = applicantName.isNew

    if (
      !hasSetDefaultApplicantRef.current[taskId] &&
      !isNewField &&
      applicantCandidateList &&
      applicantCandidateList.length === 1
    ) {
      handleChangeExistingMatchingApplicantId(`${applicantCandidateList[0].applicantId}`)
      documentOCRResultForm.setValue(
        'applicantNameCandidate.matchingApplicantId',
        `${applicantCandidateList[0].applicantId}`
      )
      hasSetDefaultApplicantRef.current[taskId] = true
    }
  }

  useEffect(
    function loadDocumentOCRResultData() {
      if (currentOCRResult) {
        documentOCRResultForm.reset(currentOCRResult)
        setFormErrors(currentOCRResult)
        if (taskIdSearchParam) {
          setDefaultApplicant(taskIdSearchParam, currentOCRResult)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOCRResult]
  )

  useEffect(
    function storeDocumentOCRResultData() {
      if (!documentOCRResultData || !taskIdSearchParam) {
        return
      }

      // NOTE: prevent existing data from being overwritten
      if (ocrResults[taskIdSearchParam]) {
        return
      }

      setOCRResult(taskIdSearchParam, documentOCRResultData)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentOCRResultData, taskIdSearchParam]
  )

  return {
    documentOCRResultForm,
    applicantCandidateList,
    serviceCenterList,
    caseCategoryList,
    hasMatchingApplicant,
    isReviewed,
    isDisabledReloadButton,
    isDisabledCompleteReviewButton,
    isOpenExtractedInfoGuideDialog,
    isReloadingCaseData,
    isUpdatingOCRReviewStatus,
    openExtractInfoGuideDialog,
    handleChangeExtractedInfoGuideDialogOpen,
    handleChangeCaseNumber,
    handleChangeNewApplicantName,
    handleChangeExistingOcrScannedApplicantName,
    handleChangeExistingMatchingApplicantId,
    handleChangeApplicationFiledDate,
    handleChangeApplicantEmail,
    handleChangeApplicantPhone,
    handleChangeCaseType,
    handleChangeServiceCenter,
    handleChangeNationality,
    handleSelectPriorityDate,
    handleSelectValidFrom,
    handleSelectValidTo,
    handleSelectRfeDueDate,
    handleSelectInterviewDate,
    handleSelectBiometricDate,
    handleChangeCaseTags,
    handleChangeCaseCategory,
    handleClickReloadButton,
    handleClickCompleteReviewButton,
  }
}
